import { initVueApp } from '~/lib/utils/vue3compat/init_vue_app';
import { addShortcutsExtension } from '~/behaviors/shortcuts';
import ShortcutsNavigation from '~/behaviors/shortcuts/shortcuts_navigation';
import { initFindFileShortcut } from '~/projects/behaviors';
import initClustersDeprecationAlert from '~/projects/clusters_deprecation_alert';
import leaveByUrl, { NAMESPACE_TYPES } from '~/namespaces/leave_by_url';
import { initUploadFileTrigger } from '~/projects/upload_file';
import initReadMore from '~/read_more';
import initAmbiguousRefModal from '~/vue_shared/components/ref/init_ambiguous_ref_modal';
import EmptyProject from '~/pages/projects/show/empty_project';
import initHeaderApp from '~/repository/init_header_app';
import { initWebIdeLink } from '~/pages/projects/shared/web_ide_link/init_web_ide_link';
import CompactCodeDropdown from 'ee_else_ce/repository/components/code_dropdown/compact_code_dropdown.vue';
import { convertObjectPropsToCamelCase, parseBoolean } from '~/lib/utils/common_utils';
import apolloProvider from '~/repository/graphql';
import { renderGFM } from '~/behaviors/markdown/render_gfm';
import { initHomePanel } from '~/projects/home_panel';
import * as Sentry from '~/sentry/sentry_browser_wrapper';

// Project show page loads different overview content based on user preferences
if (document.getElementById('js-tree-list')) {
  if (gon.features?.vue3MigrateRepository) {
    (async () => {
      try {
        const { default: initTree } = await import(
          /* webpackChunkName: 'treeList' */ 'ee_else_ce/repository?vue3'
        );
        initTree();
        return;
      } catch (e) {
        Sentry.captureException(e);
      }

      try {
        const { default: initTree } = await import(
          /* webpackChunkName: 'treeList' */ 'ee_else_ce/repository'
        );
        initTree();
      } catch {
        // Ignore fallback errors
      }
    })();
  } else {
    import(/* webpackChunkName: 'treeList' */ 'ee_else_ce/repository')
      .then(({ default: initTree }) => {
        initTree();
      })
      .catch(() => {});
  }
}

if (document.querySelector('.blob-viewer')) {
  if (gon.features?.vue3MigrateRepository) {
    (async () => {
      try {
        const { BlobViewer } = await import(
          /* webpackChunkName: 'blobViewer' */ '~/blob/viewer?vue3'
        );
        new BlobViewer(); // eslint-disable-line no-new
        initHeaderApp({ isReadmeView: true });
        return;
      } catch (e) {
        Sentry.captureException(e);
      }

      try {
        const { BlobViewer } = await import(/* webpackChunkName: 'blobViewer' */ '~/blob/viewer');
        new BlobViewer(); // eslint-disable-line no-new
        initHeaderApp({ isReadmeView: true });
      } catch {
        // Ignore fallback errors
      }
    })();
  } else {
    import(/* webpackChunkName: 'blobViewer' */ '~/blob/viewer')
      .then(({ BlobViewer }) => {
        new BlobViewer(); // eslint-disable-line no-new
        initHeaderApp({ isReadmeView: true });
      })
      .catch(() => {});
  }
}

if (document.querySelector('.project-show-activity')) {
  import(/* webpackChunkName: 'activitiesList' */ '~/activities')
    .then(({ default: Activities }) => {
      new Activities(); // eslint-disable-line no-new
    })
    .catch(() => {});
}

addShortcutsExtension(ShortcutsNavigation);

initUploadFileTrigger();
initClustersDeprecationAlert();
initReadMore();
initAmbiguousRefModal();
initHomePanel();

if (document.querySelector('.js-autodevops-banner')) {
  import(/* webpackChunkName: 'userCallOut' */ '~/user_callout')
    .then(({ default: UserCallout }) => {
      // eslint-disable-next-line no-new
      new UserCallout({
        setCalloutPerProject: false,
        className: 'js-autodevops-banner',
      });
    })
    .catch(() => {});
}

leaveByUrl(NAMESPACE_TYPES.PROJECT);

const initCodeDropdown = () => {
  const codeDropdownEl = document.querySelector('#js-project-show-empty-page #js-code-dropdown');

  if (!codeDropdownEl) return false;

  const {
    sshUrl,
    httpUrl,
    kerberosUrl,
    xcodeUrl,
    ideData,
    directoryDownloadLinks,
    showNoSshKeyMessage,
    userSettingsSshKeysPath,
    newWorkspacePath,
    projectId,
    projectPath,
    organizationId,
  } = codeDropdownEl.dataset;

  const { gitpodEnabled, showWebIdeButton, showGitpodButton, webIdeUrl, gitpodUrl } =
    convertObjectPropsToCamelCase(ideData ? JSON.parse(ideData) : {});

  return initVueApp({
    el: codeDropdownEl,
    name: 'CompactCodeDropdownRoot',
    provide: { newWorkspacePath, organizationId },
    apolloProvider,
    component: CompactCodeDropdown,
    props: {
      sshUrl,
      httpUrl,
      kerberosUrl,
      xcodeUrl,
      webIdeUrl,
      gitpodUrl,
      showWebIdeButton,
      isGitpodEnabledForInstance: parseBoolean(showGitpodButton),
      isGitpodEnabledForUser: parseBoolean(gitpodEnabled),
      directoryDownloadLinks: directoryDownloadLinks ? JSON.parse(directoryDownloadLinks) : [],
      showNoSshKeyMessage: parseBoolean(showNoSshKeyMessage),
      userSettingsSshKeysPath,
      projectId,
      projectPath,
    },
  });
};

const initEmptyProjectTabs = () => {
  const emptyProjectEl = document.querySelector('#js-project-show-empty-page');

  if (!emptyProjectEl) return;

  new EmptyProject(); // eslint-disable-line no-new
};

const initWikiContent = () => {
  const el = document.querySelector('.js-wiki-content');
  if (!el) return;

  renderGFM(el);
};

initCodeDropdown();
initFindFileShortcut();
initEmptyProjectTabs();
initWebIdeLink({ el: document.getElementById('js-tree-web-ide-link') });
initWikiContent();
